import React from 'react';

/**
 * Shared Button primitive — codifies the inline patterns already dominant in
 * the codebase (surveyed 2026-07-18) instead of inventing new design:
 *
 *   primary    bg-primary-600 hover:bg-primary-700 text-white      (~57 inline uses + .btn-primary)
 *   cta        bg-[var(--slate)] hover:bg-[var(--slate)] text-white        (~122 uses — modal/dialog CTAs)
 *   secondary  bordered neutral                                    (~163 uses)
 *   danger     bg-[var(--coral)] hover:bg-[var(--coral)] text-white              (~59 uses)
 *   ghost      borderless quiet action
 *
 * Dark mode: variants use plain light-mode classes on purpose — the unlayered
 * `.dark` overrides in src/index.css remap them (the auto-remap mechanism).
 * Never add explicit `dark:bg-gray-*` here; see the Token System page.
 */

type Variant = 'primary' | 'cta' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const VARIANT: Record<Variant, string> = {
  primary: 'bg-primary-600 hover:bg-primary-700 text-white',
  cta: 'bg-[var(--slate)] hover:bg-[var(--slate)] text-white',
  secondary: 'border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)]',
  ghost: 'text-[var(--text-sec)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]',
  danger: 'bg-[var(--coral)] hover:bg-[var(--coral)] text-white',
};

const SIZE: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', type = 'button', className = '', ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={`inline-flex items-center justify-center gap-2  font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none ${VARIANT[variant]} ${SIZE[size]} ${className}`}
      {...rest}
    />
  );
});
